import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text } from 'grommet';
import { AppButton } from '@Components/Control';
import {
  ProductFormSectionWrapper,
  ProductFormFieldContainer,
} from '@Components/Partial/Product/NewSearch';

/**
 * Column definitions for manual claim CSV (customer submit)
 * — mirrors upload assets field guide pattern.
 */
export const MANUAL_CLAIM_CSV_REQUIRED = [
  { key: 'Asset ID', description: 'YouTube Content ID asset identifier.' },
  { key: 'Infringing URL', description: 'URL of the video to claim against.' },
  { key: 'Original Link', description: 'Your original content reference URL.' },
  { key: 'Claim Type', description: 'monetize or takedown.' },
];

export const MANUAL_CLAIM_CSV_OPTIONAL = [
  { key: 'Match Type', description: 'audio, video, or audiovisual. Defaults to audiovisual if left empty.' },
  {
    key: 'Match Timeframe',
    description:
      'Optional. Full Match, a single range like 01:25-02:35, '
      + 'or multiple ranges like 01:00-02:00|03:30-04:00 (pipe-separated).',
  },
  { key: 'Channel Link', description: 'Uploader channel URL when relevant.' },
];

const ManualClaimCsvFieldGuide = ({
  small,
  page,
  button,
  onDownloadTemplate,
}) => (
  <ProductFormSectionWrapper
    small={small}
    sectionBg={page.altBg}
    sectionTitle="CSV Format & Field Guide"
    titleColor={page.primaryText}
    boxStyle={!small ? { flex: 1, minWidth: 0 } : {}}
  >
    <ProductFormFieldContainer
      small={small}
      primaryText={page.primaryText}
      hintText={page.hintText}
    >
      <Box gap="0.75rem" width="100%">
        <Box gap="0.75rem">
          <Box pad={{ bottom: '0.25rem' }} border={{ side: 'bottom', color: page.tableBorder }}>
            <Text size="1rem" weight={700} color={page.primaryText}>
              Required columns
            </Text>
          </Box>
          {MANUAL_CLAIM_CSV_REQUIRED.map((h) => (
            <Box key={h.key} direction="row">
              <Text size="0.9rem" color={page.primaryText} weight={600}>
                {h.key}
                :&nbsp;
                <Text size="0.875rem" weight={400}>{h.description}</Text>
              </Text>
            </Box>
          ))}
        </Box>
        <Box gap="0.75rem">
          <Box pad={{ bottom: '0.25rem' }} border={{ side: 'bottom', color: page.tableBorder }}>
            <Text size="1rem" weight={700} color={page.primaryText}>
              Optional columns
            </Text>
          </Box>
          {MANUAL_CLAIM_CSV_OPTIONAL.map((h) => (
            <Box key={h.key} direction="column" gap="0.25rem">
              <Text size="0.9rem" color={page.primaryText} weight={600}>
                {h.key}
                :&nbsp;
                <Text size="0.875rem" weight={400}>{h.description}</Text>
              </Text>
            </Box>
          ))}
        </Box>
        {onDownloadTemplate && button && (
          <Box margin={{ top: '1rem' }}>
            <AppButton
              overrideHover
              level="dynamicLarge"
              width="14rem"
              color={button.primaryText}
              bgColor={button.primaryBg}
              fontWeight={600}
              label="Download CSV Template"
              onClick={onDownloadTemplate}
            />
          </Box>
        )}
      </Box>
    </ProductFormFieldContainer>
  </ProductFormSectionWrapper>
);

ManualClaimCsvFieldGuide.propTypes = {
  small: PropTypes.bool.isRequired,
  page: PropTypes.shape({
    altBg: PropTypes.string.isRequired,
    primaryText: PropTypes.string.isRequired,
    hintText: PropTypes.string.isRequired,
    tableBorder: PropTypes.string.isRequired,
  }).isRequired,
  button: PropTypes.shape({
    primaryText: PropTypes.string.isRequired,
    primaryBg: PropTypes.string.isRequired,
  }),
  onDownloadTemplate: PropTypes.func,
};

ManualClaimCsvFieldGuide.defaultProps = {
  button: undefined,
  onDownloadTemplate: undefined,
};

export default ManualClaimCsvFieldGuide;
